// EssayLibraryPicker — modal que mostra os temas da Biblioteca de Redação
// e renderiza cada tema escolhido no formato "caderno de prova" pra simular
// a experiência de receber o caderno na hora.
// [[project-essay-themes-library]]
//
// 2 estágios:
//   1) Lista de cards (1 por examEditionKey, agrupando propostas alternativas)
//   2) Caderno aberto do tema escolhido, com Aplicar este tema (cria sessão)

import { useEffect, useState } from "preact/hooks";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  ExternalLink,
  Image as ImageIcon,
  Layers,
  Loader2,
  PenSquare,
  Sparkles,
  Table as TableIcon,
  X
} from "lucide-react";
import {
  api,
  type StudentEssayThemeListItem,
  type StudentEssayThemeFull,
  type StudentEssayFolder
} from "./api";
import { renderEssayMarkdown } from "@app/ui/lib/essay-markdown";

interface Props {
  onPicked: (sessionId: string) => void;
  onCancel: () => void;
}

interface Group {
  examEditionKey: string;
  themes: StudentEssayThemeListItem[];
}

interface ListState {
  phase: "loading" | "ready" | "error";
  themes: StudentEssayThemeListItem[];
  groups: Group[];
  ungrouped: StudentEssayThemeListItem[];
  folders: StudentEssayFolder[];
  err: string | null;
}

/** Filtro ativo da pasta. "all" = todas, "none" = sem pasta, string = id da pasta. */
type FolderFilter = "all" | "none" | string;

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return iso;
  }
}

/** Renderiza markdown e adiciona "Fonte: " antes de strings que se parecem
 *  com referência bibliográfica quando não tem prefix. Conservador: só
 *  prefixa se o source não estiver vazio. */
function FonteLine({ source }: { source: string }) {
  if (!source.trim()) return null;
  const stripped = source.trim();
  const alreadyHasFonte = /^(fonte|font|f\.)\s*:/i.test(stripped);
  return (
    <p
      class="mt-2 text-[11px] italic text-muted-foreground"
      dangerouslySetInnerHTML={{
        __html:
          (alreadyHasFonte ? "" : "<strong>Fonte:</strong> ") +
          renderEssayMarkdown(stripped)
      }}
    />
  );
}

function FolderChip({
  label,
  count,
  active,
  onClick
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
        active
          ? "bg-violet-500 text-white"
          : "bg-muted text-muted-foreground hover:bg-muted/70"
      }`}
    >
      {label}
      <span
        class={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
          active ? "bg-white/20" : "bg-background/60"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function ThemeListCard({
  group,
  onPick
}: {
  group: { themes: StudentEssayThemeListItem[] };
  onPick: (id: string) => void;
}) {
  const lead = group.themes[0];
  const others = group.themes.slice(1);
  return (
    <article class="rounded-lg border border-border bg-card p-4 transition-shadow hover:shadow-md">
      <header class="mb-2 flex items-start justify-between gap-2">
        <div class="min-w-0">
          <h3 class="truncate text-sm font-bold">{lead.displayName || lead.title}</h3>
          <p class="line-clamp-2 text-xs text-muted-foreground">{lead.title}</p>
        </div>
        <span class="shrink-0 rounded bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
          {lead.banca}
        </span>
      </header>
      <div class="mb-3 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
        {[
          lead.applicationDate ? (
            <span class="inline-flex items-center gap-1">
              <Calendar className="size-3" />
              {fmtDate(lead.applicationDate)}
            </span>
          ) : null,
          lead.phase,
          lead.edition
        ]
          .filter(Boolean)
          .map((piece, i, arr) => (
            <span key={i} class="inline-flex items-center">
              {piece}
              {i < arr.length - 1 ? <span class="mx-1.5 opacity-50">·</span> : null}
            </span>
          ))}
      </div>
      {group.themes.length > 1 && (
        <p class="mb-2 inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
          <Layers className="size-3" />
          {group.themes.length} propostas alternativas da mesma prova
        </p>
      )}
      <div class="flex flex-col gap-1">
        {group.themes.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onPick(t.id)}
            class="rounded border border-border bg-background px-3 py-2 text-left text-xs transition-colors hover:border-violet-500 hover:bg-violet-500/5"
          >
            <span class="font-semibold">{t.title}</span>
            {t.edition && <span class="ml-1 text-muted-foreground">— {t.edition}</span>}
          </button>
        ))}
      </div>
    </article>
  );
}

function CadernoView({
  theme,
  onBack,
  onApply,
  applying
}: {
  theme: StudentEssayThemeFull;
  onBack: () => void;
  onApply: () => void;
  applying: boolean;
}) {
  return (
    <div class="space-y-4">
      <button
        type="button"
        onClick={onBack}
        class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={12} />
        Voltar à lista
      </button>

      {/* Caderno simulado — fundo creme/sépia + fonte do projeto (Inter via .essay-paper) */}
      <article class="essay-paper rounded-lg border border-border bg-[#fdfcf7] p-6 text-[#1a1815] shadow-inner dark:bg-stone-900/50 dark:text-stone-100">
        <header class="mb-6 border-b border-stone-300/50 pb-3 text-center dark:border-stone-700/50">
          <p class="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500">
            {theme.banca}
          </p>
          <h1 class="mt-1 text-xl font-bold tracking-tight">{theme.displayName || theme.title}</h1>
          {theme.applicationDate && (
            <p class="mt-0.5 text-xs text-stone-500">
              Aplicada em {fmtDate(theme.applicationDate)}
            </p>
          )}
          {theme.originalSourceUrl && (
            <a
              href={theme.originalSourceUrl}
              target="_blank"
              rel="noreferrer"
              class="mt-1 inline-flex items-center gap-1 text-[10px] text-violet-600 hover:underline dark:text-violet-400"
            >
              Ver caderno oficial <ExternalLink size={10} />
            </a>
          )}
        </header>

        {/* Instruções oficiais */}
        {theme.officialInstructions && (
          <section class="mb-6 rounded border-l-4 border-amber-400 bg-amber-50/60 px-4 py-3 text-[13px] dark:bg-amber-900/15">
            <div
              dangerouslySetInnerHTML={{
                __html: renderEssayMarkdown(theme.officialInstructions)
              }}
            />
          </section>
        )}

        {/* Statement / Proposta */}
        {theme.statement && (
          <section class="mb-6">
            <h2 class="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
              Proposta de Redação
            </h2>
            <div
              class="text-[14px]"
              dangerouslySetInnerHTML={{
                __html: renderEssayMarkdown(theme.statement)
              }}
            />
          </section>
        )}

        {/* Direcionadores */}
        {theme.directives.length > 0 && (
          <section class="mb-6 rounded border border-stone-300/50 bg-stone-100/50 px-4 py-3 text-[12px] dark:border-stone-700/50 dark:bg-stone-800/30">
            <p class="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
              Direcionadores
            </p>
            <ul class="essay-ul">
              {theme.directives.map((d, i) => (
                <li
                  key={i}
                  dangerouslySetInnerHTML={{ __html: renderEssayMarkdown(d).replace(/^<p[^>]*>|<\/p>$/g, "") }}
                />
              ))}
            </ul>
          </section>
        )}

        {/* Fragmentos */}
        {theme.fragments.length > 0 && (
          <section class="space-y-5">
            <h2 class="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
              Coletânea
            </h2>
            {theme.fragments.map((frag) => (
              <div key={frag.id} class="space-y-2">
                {frag.label && (
                  <h3
                    class="text-[11px] font-bold uppercase tracking-[0.15em] text-stone-500"
                    dangerouslySetInnerHTML={{
                      __html: renderEssayMarkdown(frag.label)
                    }}
                  />
                )}
                <div class="space-y-3">
                  {frag.items.map((it) => (
                    <div key={it.id}>
                      {it.kind === "text" && it.content && (
                        <div
                          class="text-[13px]"
                          dangerouslySetInnerHTML={{
                            __html: renderEssayMarkdown(it.content)
                          }}
                        />
                      )}
                      {it.kind === "image" && it.imageDataUrl && (
                        <figure class="my-2">
                          <img
                            src={it.imageDataUrl}
                            alt={it.content || ""}
                            class="mx-auto max-h-80 rounded border border-stone-300/50 dark:border-stone-700/50"
                          />
                          {it.content && (
                            <figcaption class="mt-1 text-center text-[11px] italic text-stone-500">
                              {it.content}
                            </figcaption>
                          )}
                        </figure>
                      )}
                      {it.kind === "image" && !it.imageDataUrl && (
                        <div class="my-2 rounded border border-dashed border-stone-300/70 bg-stone-100/50 p-4 text-center text-[11px] text-stone-500 dark:border-stone-700/50 dark:bg-stone-800/30">
                          <ImageIcon size={20} class="mx-auto mb-1 opacity-50" />
                          [imagem pendente de upload]
                          {it.content && <p class="mt-1">{it.content}</p>}
                        </div>
                      )}
                      {it.kind === "table" && it.tableData && (
                        <div class="my-2 overflow-x-auto">
                          {it.tableData.caption && (
                            <p class="mb-1 text-center text-[12px] font-semibold">
                              {it.tableData.caption}
                            </p>
                          )}
                          <table class="w-full border-collapse text-[12px]">
                            <thead>
                              <tr>
                                {(it.tableData.headers ?? []).map((h, i) => (
                                  <th
                                    key={i}
                                    class="border border-stone-400/50 bg-stone-100/70 px-2 py-1 text-left font-semibold dark:bg-stone-800/40"
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(it.tableData.rows ?? []).map((row, ri) => (
                                <tr key={ri}>
                                  {row.map((cell, ci) => (
                                    <td
                                      key={ci}
                                      class="border border-stone-400/50 px-2 py-1"
                                    >
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {it.source && <FonteLine source={it.source} />}
                    </div>
                  ))}
                </div>
                {frag.sourceOverall && <FonteLine source={frag.sourceOverall} />}
              </div>
            ))}
          </section>
        )}
      </article>

      <div class="sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
        <button
          type="button"
          onClick={onBack}
          class="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={onApply}
          disabled={applying}
          class="flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {applying ? <Loader2 size={14} class="animate-spin" /> : <Sparkles size={14} />}
          Aplicar este tema
        </button>
      </div>
    </div>
  );
}

export function EssayLibraryPicker({ onPicked, onCancel }: Props) {
  const [list, setList] = useState<ListState>({
    phase: "loading",
    themes: [],
    groups: [],
    ungrouped: [],
    folders: [],
    err: null
  });
  const [selected, setSelected] = useState<StudentEssayThemeFull | null>(null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [folderFilter, setFolderFilter] = useState<FolderFilter>("all");

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.listEssayThemesLibrary();
        setList({
          phase: "ready",
          themes: r.themes,
          groups: r.groups,
          ungrouped: r.ungrouped,
          folders: r.folders ?? [],
          err: null
        });
      } catch (e) {
        setList({
          phase: "error",
          themes: [],
          groups: [],
          ungrouped: [],
          folders: [],
          err: e instanceof Error ? e.message : String(e)
        });
      }
    })();
  }, []);

  async function pickTheme(id: string) {
    setLoadingSelected(true);
    setErr(null);
    try {
      const full = await api.getEssayThemeLibrary(id);
      setSelected(full);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingSelected(false);
    }
  }

  async function applySelected() {
    if (!selected) return;
    setApplying(true);
    setErr(null);
    try {
      const r = await api.applyEssayThemeLibrary(selected.id);
      onPicked(r.sessionId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setApplying(false);
    }
  }

  // Filtra os themes da pasta ativa antes de montar groups.
  const themesAfterFilter: StudentEssayThemeListItem[] =
    folderFilter === "all"
      ? list.themes
      : folderFilter === "none"
        ? list.themes.filter((t) => !t.libraryFolderId)
        : list.themes.filter((t) => t.libraryFolderId === folderFilter);

  // Reagrupa pelo examEditionKey (mesma lógica do backend mas só com os themes
  // filtrados). Mantém a UX atual onde proposta1 + proposta2 da MESMA prova
  // ficam num card só.
  const byEdition = new Map<string, StudentEssayThemeListItem[]>();
  const filteredUngrouped: StudentEssayThemeListItem[] = [];
  for (const t of themesAfterFilter) {
    if (!t.examEditionKey) {
      filteredUngrouped.push(t);
      continue;
    }
    const arr = byEdition.get(t.examEditionKey) ?? [];
    arr.push(t);
    byEdition.set(t.examEditionKey, arr);
  }
  const allGroups: Group[] = [
    ...Array.from(byEdition.entries()).map(([examEditionKey, themes]) => ({
      examEditionKey,
      themes
    })),
    ...filteredUngrouped.map((t) => ({ examEditionKey: t.id, themes: [t] }))
  ];

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div class="flex h-[90vh] w-full max-w-5xl flex-col rounded-lg border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <div class="flex items-center gap-2">
            <BookOpen className="size-5 text-violet-500" />
            <h2 class="text-base font-bold">
              {selected ? selected.displayName || selected.title : "Biblioteca de Redação"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Fechar"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 overflow-y-auto p-4">
          {err && (
            <div class="mb-3 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {err}
            </div>
          )}

          {!selected && list.phase === "loading" && (
            <div class="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span class="ml-2 text-sm">Carregando temas…</span>
            </div>
          )}

          {!selected && list.phase === "error" && (
            <div class="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              Erro: {list.err}
            </div>
          )}

          {!selected && list.phase === "ready" && list.themes.length === 0 && (
            <div class="py-12 text-center text-sm text-muted-foreground">
              <PenSquare className="mx-auto mb-2 size-8 opacity-50" />
              Nenhum tema disponível na biblioteca ainda.
            </div>
          )}

          {!selected && list.phase === "ready" && list.themes.length > 0 && (
            <>
              {/* Chips de filtro por pasta — só mostra se houver pelo menos
                  uma pasta com tema acessível. */}
              {list.folders.length > 0 && (
                <div class="mb-4 flex flex-wrap items-center gap-1.5">
                  <FolderChip
                    label="Todas"
                    count={list.themes.length}
                    active={folderFilter === "all"}
                    onClick={() => setFolderFilter("all")}
                  />
                  {list.folders.map((f) => (
                    <FolderChip
                      key={f.id}
                      label={f.name}
                      count={f.themeCount}
                      active={folderFilter === f.id}
                      onClick={() => setFolderFilter(f.id)}
                    />
                  ))}
                  {list.themes.some((t) => !t.libraryFolderId) && (
                    <FolderChip
                      label="Sem categoria"
                      count={list.themes.filter((t) => !t.libraryFolderId).length}
                      active={folderFilter === "none"}
                      onClick={() => setFolderFilter("none")}
                    />
                  )}
                </div>
              )}
              {allGroups.length === 0 ? (
                <div class="py-12 text-center text-sm text-muted-foreground">
                  Nenhum tema nesta pasta.
                </div>
              ) : (
                <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {allGroups.map((g) => (
                    <ThemeListCard
                      key={g.examEditionKey}
                      group={g}
                      onPick={(id) => void pickTheme(id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {loadingSelected && (
            <div class="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              <span class="ml-2 text-sm">Abrindo caderno…</span>
            </div>
          )}

          {selected && !loadingSelected && (
            <CadernoView
              theme={selected}
              onBack={() => setSelected(null)}
              onApply={() => void applySelected()}
              applying={applying}
            />
          )}
        </div>
      </div>
    </div>
  );
}
